import { useMemo, useState } from "react";
import { AlertCircle, ClipboardList, Info } from "lucide-react";
import { Skeleton } from "@bookdental/ui";
import { cn } from "@/lib/cn";
import { trpc } from "@/lib/trpc";
import { formatBRL } from "./prosthesis-meta";
import { CartaoDoDentista, type DentistaDosPedidos } from "./CartaoDoDentista";
import { PedidosDoDentistaDialog } from "./PedidosDoDentistaDialog";

const PERIODOS = [
  { chave: "mes", rotulo: "Este mês" },
  { chave: "90d", rotulo: "90 dias" },
  { chave: "tudo", rotulo: "Tudo" },
] as const;

type Periodo = (typeof PERIODOS)[number]["chave"];

function inicioDoPeriodo(p: Periodo): Date | undefined {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (p === "mes") {
    d.setDate(1);
    return d;
  }
  if (p === "90d") {
    d.setDate(d.getDate() - 90);
    return d;
  }
  return new Date(0);
}

/**
 * Pedidos de prótese por dentista responsável.
 *
 * Responde uma pergunta diferente da dos cartões da Equipe. Lá é tempo real — "dá pra contar com
 * essa pessoa agora?". Aqui é carga e custo: quanto cada um pediu, quanto está em aberto e quanto
 * está atrasado. Mesma moldura visual, números de outra natureza — copiar os da Equipe traria a
 * forma certa com o conteúdo errado.
 *
 * Conta por `ProsthesisOrder.dentistaId`: quando a recepção registra o pedido, ela escolhe o
 * dentista, e o pedido conta pra ele, não pra recepção.
 */
/** `acao`: o botão de voltar pra "Todos os pedidos", que no celular mora ao lado deste título. */
export function ProtesePorDentista({ acao }: { acao?: React.ReactNode } = {}) {
  const [periodo, setPeriodo] = useState<Periodo>("mes");
  const [aberto, setAberto] = useState<DentistaDosPedidos | null>(null);
  const query = trpc.prosthesis.porAutor.useQuery({ desde: inicioDoPeriodo(periodo) });

  const autores = useMemo(() => query.data?.autores ?? [], [query.data]);
  const totais = useMemo(
    () =>
      autores.reduce(
        (acc, a) => ({
          emAberto: acc.emAberto + a.emAberto,
          atrasados: acc.atrasados + a.atrasados,
          noPeriodo: acc.noPeriodo + a.noPeriodo,
          valor: acc.valor + (a.valorNoPeriodoCents ?? 0),
        }),
        { emAberto: 0, atrasados: 0, noPeriodo: 0, valor: 0 },
      ),
    [autores],
  );

  if (query.error) {
    return (
      <div className="rounded-xl border border-zinc-200 px-6 py-10 text-center dark:border-border">
        <AlertCircle className="mx-auto h-8 w-8 text-red-400" />
        <p className="mt-3 text-sm font-medium text-zinc-900 dark:text-foreground">
          Não foi possível carregar os pedidos por dentista
        </p>
        <p className="mt-1 text-xs text-zinc-500 dark:text-muted-foreground">{query.error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex w-full items-start justify-between gap-3 sm:w-auto">
          <div>
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Pedidos por dentista</h3>
            <p className="mt-0.5 text-xs text-zinc-500 dark:text-muted-foreground">
              Quanto cada dentista tem em aberto e quanto venceu o prazo. Toque no cartão para ver os pedidos.
            </p>
          </div>
          {acao}
        </div>
        <div className="flex gap-1" role="group" aria-label="Período">
          {PERIODOS.map((p) => (
            <button
              key={p.chave}
              type="button"
              onClick={() => setPeriodo(p.chave)}
              aria-pressed={periodo === p.chave}
              className={cn(
                "inline-flex h-8 items-center rounded-md px-2.5 text-xs font-medium transition-colors",
                periodo === p.chave
                  ? "bg-brand-600 font-semibold text-white"
                  : "text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted",
              )}
            >
              {p.rotulo}
            </button>
          ))}
        </div>
      </div>

      {query.isLoading ? (
        <div className="grid gap-3 md:grid-cols-2">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-[132px] w-full rounded-xl" />
          ))}
        </div>
      ) : autores.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-200 bg-zinc-50/50 p-8 text-center dark:border-border dark:bg-muted/20">
          <ClipboardList className="mx-auto h-10 w-10 text-zinc-300 dark:text-muted-foreground/40" />
          <p className="mt-3 text-sm font-medium text-zinc-700 dark:text-foreground">
            Nenhum pedido de prótese ainda
          </p>
          <p className="mt-1 text-xs text-zinc-500 dark:text-muted-foreground">
            Assim que alguém enviar um pedido, ele aparece aqui separado por dentista.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            <Chip rotulo="Em aberto" valor={totais.emAberto} />
            <Chip rotulo="Atrasados" valor={totais.atrasados} alerta={totais.atrasados > 0} />
            <Chip rotulo="No período" valor={totais.noPeriodo} />
            {query.data?.mostraValor && <Chip rotulo="Valor no período" valor={formatBRL(totais.valor)} />}
          </div>

          <div className="grid items-stretch gap-3 md:grid-cols-2">
            {autores.map((a) => (
              <CartaoDoDentista
                key={a.userId ?? "sem-autor"}
                dentista={a}
                mostraValor={!!query.data?.mostraValor}
                onAbrir={() => setAberto(a)}
              />
            ))}
          </div>

          <p className="flex items-start gap-1.5 text-[11px] text-zinc-400 dark:text-muted-foreground/80">
            <Info className="mt-px h-3 w-3 shrink-0" />
            {/* Um span só: soltos, texto e <strong> viravam itens do flex e quebravam em colunas. */}
            <span>
              Conta pelo <strong className="font-semibold">dentista responsável</strong>. Pedido registrado pela
              recepção conta pro dentista escolhido no pedido.
            </span>
          </p>
        </>
      )}

      <PedidosDoDentistaDialog dentista={aberto} onClose={() => setAberto(null)} />
    </div>
  );
}

function Chip({ rotulo, valor, alerta }: { rotulo: string; valor: number | string; alerta?: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs",
        alerta
          ? "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
          : "border-zinc-200 bg-white text-zinc-600 dark:border-border dark:bg-card dark:text-muted-foreground",
      )}
    >
      {rotulo}
      <strong className="font-semibold tabular-nums text-zinc-900 dark:text-foreground">{valor}</strong>
    </span>
  );
}
